import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useLocalization } from "@fluent/react";
import {
  Button,
  Dialog,
  Pagination,
  ResponsiveFlex,
  Tabs,
} from "@narmi/design_system";
import { useNotificationContext } from "cerulean";
import { useSearchParams } from "react-router-dom";

import PageLayout from "../../components/PageLayout";

import BulkActionSnackbar from "./components/BulkActionSnackbar";
import EmptyState from "./components/EmptyState";
import MatchedReceiptDrawer from "./components/MatchedReceiptDrawer/MatchedReceiptDrawer";
import MatchedTable from "./components/MatchedTable";
import ReceiptDetailsDrawer from "./components/ReceiptDetailsDrawer/ReceiptDetailsDrawer";
import ReceiptsSortDropdown from "./components/ReceiptsSortDropdown/ReceiptsSortDropdown";
import ReceiptsTable from "./components/ReceiptsTable";
import {
  SelectionContext,
  SelectionProvider,
} from "./components/SelectionContext";
import StatCard from "./components/StatCard";
import UnmatchedReceiptDrawer from "./components/UnmatchedReceiptDrawer/UnmatchedReceiptDrawer";
import UnmatchedTable from "./components/UnmatchedTable";
import UploadReceiptDialog from "./components/UploadReceiptDialog/UploadReceiptDialog";
import { RECEIPTS_TAB_IDS } from "./constants";
import { useAcceptMatch } from "./hooks/useAcceptMatch";
import { useCanWriteReceipts } from "./hooks/useCanWriteReceipts";
import { useBulkAcceptMatches } from "./hooks/useBulkAcceptMatches";
import { useBulkRejectMatches } from "./hooks/useBulkRejectMatches";
import {
  ReceiptBucket,
  RECEIPTS_DEFAULT_PAGE_SIZE,
  ReceiptSort,
  useReceiptsList,
  useReceiptUploadPolling,
} from "./hooks/useReceiptsList";
import { useRejectMatch } from "./hooks/useRejectMatch";
import { useUnlinkMatch } from "./hooks/useUnlinkMatch";
import styles from "./ReceiptsPage.module.scss";

import type { AcceptMatchError } from "./hooks/useAcceptMatch";
import type { BulkRejectResponse } from "./hooks/useBulkRejectMatches";
import type { ReceiptListRow, SuggestedMatch } from "./hooks/useReceiptsList";
import type { RejectMatchError } from "./hooks/useRejectMatch";
import type { UnlinkMatchError } from "./hooks/useUnlinkMatch";

const TAB_BUCKETS = [
  ReceiptBucket.PENDING_REVIEW,
  ReceiptBucket.MATCHED,
  ReceiptBucket.UNMATCHED,
] as const;

const EMPTY_COPY = [
  {
    heading: "receipts-empty-pending-heading",
    body: "receipts-empty-pending-body",
    testId: "receipts-empty-state-pending",
  },
  {
    heading: "receipts-empty-matched-heading",
    body: "receipts-empty-matched-body",
    testId: "receipts-empty-state-matched",
  },
  {
    heading: "receipts-empty-unmatched-heading",
    body: "receipts-empty-unmatched-body",
    testId: "receipts-empty-state-unmatched",
  },
] as const;

const SORT_QUERY_KEY = "sort";

const VALID_SORTS = new Set<string>(Object.values(ReceiptSort));

const parseSortParam = (raw: string | null): ReceiptSort => {
  if (raw && VALID_SORTS.has(raw)) return raw as ReceiptSort;
  return ReceiptSort.MOST_RECENT;
};

interface TabContentProps {
  rows: ReceiptListRow[];
  emptyHeading: string;
  emptyBody: string;
  emptyTestId: string;
  onRowClick?: (uuid: string) => void;
  onAcceptMatch?: (params: {
    suggestionId: number;
    receiptUuid: string;
  }) => void;
  onRejectMatch?: (params: {
    suggestionId: number;
    receiptUuid: string;
  }) => void;
  pendingAcceptSuggestionId?: number | null;
  pendingRejectSuggestionId?: number | null;
  showSelection?: boolean;
  bulkActionErrors?: Record<string, string>;
}

const TabContent = ({
  rows,
  emptyHeading,
  emptyBody,
  emptyTestId,
  onRowClick,
  onAcceptMatch,
  onRejectMatch,
  pendingAcceptSuggestionId,
  pendingRejectSuggestionId,
  showSelection,
  bulkActionErrors,
}: TabContentProps) => {
  if (rows.length === 0) {
    return (
      <EmptyState
        heading={emptyHeading}
        body={emptyBody}
        testId={emptyTestId}
      />
    );
  }
  return (
    <ReceiptsTable
      rows={rows}
      onRowClick={onRowClick}
      onAcceptMatch={onAcceptMatch}
      onRejectMatch={onRejectMatch}
      pendingAcceptSuggestionId={pendingAcceptSuggestionId}
      pendingRejectSuggestionId={pendingRejectSuggestionId}
      showSelection={showSelection}
      bulkActionErrors={bulkActionErrors}
    />
  );
};

const ReceiptsPageContent = () => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const { selectionState, setSelectionState } = useContext(SelectionContext);
  const canWrite = useCanWriteReceipts();
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [pollForUuid, setPollForUuid] = useState<string | undefined>(undefined);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [selectedReceiptUuid, setSelectedReceiptUuid] = useState<string | null>(
    null
  );
  const [selectedMatchedUuid, setSelectedMatchedUuid] = useState<string | null>(
    null
  );
  // Captured at click time so the matched-transaction card survives list
  // refetches and tab changes that would otherwise drop the row out of scope.
  const [selectedMatchedTransaction, setSelectedMatchedTransaction] =
    useState<SuggestedMatch | null>(null);
  const [selectedUnmatchedUuid, setSelectedUnmatchedUuid] = useState<
    string | null
  >(null);
  const [pendingUnlink, setPendingUnlink] = useState<{
    suggestionId: number;
    receiptId: string;
  } | null>(null);
  // Transient per-row error messages from the most recent bulk action,
  // keyed by receipt uuid. Cleared on tab switch and at the start of each
  // new bulk action.
  const [bulkActionErrors, setBulkActionErrors] = useState<
    Record<string, string>
  >({});
  // Captured at click time so onSuccess can advance the drawer even after the
  // optimistic removal has shrunk pendingReceiptUuids.
  const nextDrawerUuidRef = useRef<string | null | undefined>(undefined);

  const bucket = TAB_BUCKETS[activeTabIndex];
  const [searchParams, setSearchParams] = useSearchParams();
  const sort = parseSortParam(searchParams.get(SORT_QUERY_KEY));

  // Per-tab offset state. Each tab keeps its own page so switching tabs and
  // coming back preserves where the user was. Index-aligned with TAB_BUCKETS.
  const [tabOffsets, setTabOffsets] = useState<[number, number, number]>([
    0, 0, 0,
  ]);
  const limit = RECEIPTS_DEFAULT_PAGE_SIZE;

  const handleSortChange = useCallback(
    (next: ReceiptSort) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          if (next === ReceiptSort.MOST_RECENT) {
            updated.delete(SORT_QUERY_KEY);
          } else {
            updated.set(SORT_QUERY_KEY, next);
          }
          return updated;
        },
        { replace: true }
      );
      // A new sort changes which rows fall on page 1, so reset every tab's
      // offset — leaving them stale would land the user on a page that no
      // longer corresponds to anything meaningful.
      setTabOffsets([0, 0, 0]);
    },
    [setSearchParams]
  );

  // One subscription per bucket so each tab owns its own page state and React
  // Query caches the data per [bucket, offset, limit]. All three fire on
  // mount; switching tabs is free.
  const pending = useReceiptsList({
    bucket: ReceiptBucket.PENDING_REVIEW,
    offset: tabOffsets[0],
    limit,
    sort,
  });
  const matched = useReceiptsList({
    bucket: ReceiptBucket.MATCHED,
    offset: tabOffsets[1],
    limit,
    sort,
  });
  const unmatched = useReceiptsList({
    bucket: ReceiptBucket.UNMATCHED,
    offset: tabOffsets[2],
    limit,
    sort,
  });
  const bucketResults = [pending, matched, unmatched] as const;
  const activeResult = bucketResults[activeTabIndex];
  const { receipts } = activeResult;
  // Stat-card counts are bucket-agnostic on the backend, so any tab's response
  // works — drive from the active tab so the cards refresh on tab switch.
  const { counts } = activeResult;
  const { total } = activeResult;
  const offset = tabOffsets[activeTabIndex];

  const handlePageChange = useCallback(
    (nextOffset: number) => {
      setTabOffsets((prev) => {
        const next: [number, number, number] = [...prev];
        next[activeTabIndex] = nextOffset;
        return next;
      });
    },
    [activeTabIndex]
  );

  // Removing rows from a bucket (e.g. bulk accept/reject on the last page) can
  // drop its total below that tab's current offset, which would otherwise
  // strand the user on an empty page with the pagination control hidden. Clamp
  // every tab's offset to its bucket's last valid page when its total shrinks.
  useEffect(() => {
    const totals = [pending.total, matched.total, unmatched.total];
    setTabOffsets((prev) => {
      let changed = false;
      const next = prev.map((tabOffset, i) => {
        const maxOffset = Math.max(
          0,
          (Math.ceil(totals[i] / limit) - 1) * limit
        );
        if (tabOffset > maxOffset) {
          changed = true;
          return maxOffset;
        }
        return tabOffset;
      }) as [number, number, number];
      return changed ? next : prev;
    });
  }, [pending.total, matched.total, unmatched.total, limit]);

  // Surface a list-fetch failure. The query uses retry:false and
  // keepPreviousData, so without this a failed page/sort/bucket request would
  // leave the prior page on screen with no indication anything went wrong.
  //
  // Track which bucket errors have already been notified to avoid duplicate
  // toasts when the user switches tabs and comes back to an errored tab.
  const notifiedErrorsRef = useRef<Set<string>>(new Set());
  const listError = activeResult.error;
  useEffect(() => {
    if (listError) {
      const errorKey = `${bucket}:${listError}`;
      if (!notifiedErrorsRef.current.has(errorKey)) {
        notifiedErrorsRef.current.add(errorKey);
        sendNotification({
          type: "negative",
          text: l10n.getString("receipts-list-error"),
        });
      }
    }
  }, [listError, bucket, l10n, sendNotification]);

  // Bare stat label while counts are still loading, to avoid a "(0)" flash.
  const getTabLabel = (
    withCountId: string,
    bareId: string,
    count: number | undefined
  ) =>
    count === undefined
      ? l10n.getString(bareId)
      : l10n.getString(withCountId, { count });

  const {
    isVisible: isUploadedReceiptVisible,
    isExtractionFailed: isUploadedReceiptExtractionFailed,
  } = useReceiptUploadPolling(pollForUuid);

  // Ref-guard prevents the failure toast from firing more than once per
  // upload session (re-renders, query cache hits, etc.). Reset on each new
  // upload so a back-to-back failure still surfaces.
  const lastFailedToastUuidRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!pollForUuid) {
      lastFailedToastUuidRef.current = undefined;
      return;
    }
    if (!isUploadedReceiptVisible) return;
    if (
      isUploadedReceiptExtractionFailed &&
      lastFailedToastUuidRef.current !== pollForUuid
    ) {
      sendNotification({
        type: "negative",
        text: l10n.getString("receipts-upload-extraction-failed-toast"),
      });
      lastFailedToastUuidRef.current = pollForUuid;
    }
    setPollForUuid(undefined);
  }, [
    isUploadedReceiptVisible,
    isUploadedReceiptExtractionFailed,
    pollForUuid,
    sendNotification,
    l10n,
  ]);

  // Clear bulk selection (and any lingering bulk errors) when the user
  // switches tabs. Selections + errors are page-scoped and tab-scoped —
  // keeping them across buckets would surface receipts that are no longer
  // visible.
  useEffect(() => {
    setSelectionState({});
    setBulkActionErrors({});
  }, [bucket, setSelectionState]);

  // After a refetch, drop any selected uuids that are no longer present in
  // the current page of results (e.g. a receipt was confirmed and moved to
  // another bucket). Mirror the same prune for bulk-action errors so chips
  // don't linger on rows that aren't visible anymore.
  //
  // Skip pruning on the render where `bucket` just changed — the clear-on-
  // tab-switch effect above runs in the same pass and will wipe state to
  // {}. Pruning here against the prior bucket's `receipts` would otherwise
  // race the tab-switch reset.
  const prevBucketRef = useRef(bucket);
  useEffect(() => {
    if (prevBucketRef.current !== bucket) {
      prevBucketRef.current = bucket;
      return;
    }
    const visibleUuids = new Set(receipts.map((row) => row.uuid));
    setSelectionState((previous) => {
      const next: typeof previous = {};
      let changed = false;
      Object.entries(previous).forEach(([uuid, row]) => {
        if (visibleUuids.has(uuid)) {
          next[uuid] = row;
        } else {
          changed = true;
        }
      });
      return changed ? next : previous;
    });
    setBulkActionErrors((previous) => {
      const next: Record<string, string> = {};
      let changed = false;
      Object.entries(previous).forEach(([uuid, msg]) => {
        if (visibleUuids.has(uuid)) {
          next[uuid] = msg;
        } else {
          changed = true;
        }
      });
      return changed ? next : previous;
    });
  }, [receipts, bucket, setSelectionState]);

  // Drawer prev/next navigates within the active tab's visible rows. Each list
  // is populated only for its own bucket so a drawer never navigates a stale
  // list from a different tab.
  const pendingReceiptUuids = useMemo(
    () =>
      bucket === ReceiptBucket.PENDING_REVIEW
        ? receipts.map((row) => row.uuid)
        : [],
    [bucket, receipts]
  );
  const matchedReceiptUuids = useMemo(
    () =>
      bucket === ReceiptBucket.MATCHED ? receipts.map((row) => row.uuid) : [],
    [bucket, receipts]
  );
  const unmatchedReceiptUuids = useMemo(
    () =>
      bucket === ReceiptBucket.UNMATCHED ? receipts.map((row) => row.uuid) : [],
    [bucket, receipts]
  );

  const acceptMatch = useAcceptMatch({
    onSuccess: () => {
      sendNotification({
        type: "success",
        text: l10n.getString("receipts-action-accept-success"),
      });
      if (nextDrawerUuidRef.current !== undefined) {
        setSelectedReceiptUuid(nextDrawerUuidRef.current);
        nextDrawerUuidRef.current = undefined;
      }
    },
    onError: (err: AcceptMatchError) => {
      sendNotification({
        type: "negative",
        text: l10n.getString(
          err.isConflict
            ? "receipts-action-accept-conflict-error"
            : "receipts-action-accept-error"
        ),
      });
      nextDrawerUuidRef.current = undefined;
    },
  });

  const rejectMatch = useRejectMatch({
    onSuccess: () => {
      // No success toast on reject (per reject-1.4 design). Only advance
      // the drawer if one was queued.
      if (nextDrawerUuidRef.current !== undefined) {
        setSelectedReceiptUuid(nextDrawerUuidRef.current);
        nextDrawerUuidRef.current = undefined;
      }
    },
    onError: (err: RejectMatchError) => {
      sendNotification({
        type: "negative",
        text: l10n.getString(
          err.isConflict
            ? "receipts-action-reject-conflict-error"
            : "receipts-action-reject-error"
        ),
      });
      nextDrawerUuidRef.current = undefined;
    },
  });

  const unlinkMatch = useUnlinkMatch({
    onSuccess: () => {
      setSelectedMatchedUuid(null);
      setSelectedMatchedTransaction(null);
      setPendingUnlink(null);
      sendNotification({
        type: "success",
        text: l10n.getString("receipts-action-unlink-success"),
      });
    },
    onError: (err: UnlinkMatchError) => {
      setPendingUnlink(null);
      sendNotification({
        type: "negative",
        text: l10n.getString(
          err.isConflict
            ? "receipts-action-unlink-conflict-error"
            : "receipts-action-unlink-error"
        ),
      });
    },
  });

  const bulkAcceptMatches = useBulkAcceptMatches({
    onSuccess: (response, variables) => {
      const { summary, results } = response;
      // receiptUuidBySuggestionId was snapshotted at click time, so
      // failed-row chip mapping is unaffected if the refetch-prune effect
      // shrank selectionState while the request was in-flight.
      const errors: Record<string, string> = {};
      results.forEach((r) => {
        if (r.status === "failed") {
          const receiptUuid =
            variables.receiptUuidBySuggestionId[r.suggestion_id];
          if (receiptUuid) errors[receiptUuid] = r.error_message;
        }
      });
      setBulkActionErrors(errors);

      if (summary.failed === 0) {
        sendNotification({
          type: "success",
          text: l10n.getString("receipts-bulk-accept-success", {
            count: summary.succeeded,
          }),
        });
      } else {
        // Partial-failure copy doubles as the "all failed (server)" copy
        // — "0 accepted, N failed." is accurate and avoids conflating
        // per-item conflicts with a true request-level failure (handled
        // separately in onError).
        sendNotification({
          type: "negative",
          text: l10n.getString("receipts-bulk-accept-partial", {
            succeeded: summary.succeeded,
            failed: summary.failed,
          }),
        });
      }

      // Clear selections that successfully completed; the failed rows stay
      // selected so the user can retry without re-clicking.
      const succeededReceiptUuids = new Set(
        results.filter((r) => r.status === "success").map((r) => r.receipt_uuid)
      );
      setSelectionState((prev) => {
        const next: typeof prev = {};
        Object.entries(prev).forEach(([uuid, row]) => {
          if (!succeededReceiptUuids.has(uuid)) next[uuid] = row;
        });
        return next;
      });
    },
    onError: () => {
      // Request-level failure (network / 4xx on shape). Per-item state
      // unchanged; "Couldn't accept any matches" is the right copy here
      // because nothing actually got attempted server-side.
      sendNotification({
        type: "negative",
        text: l10n.getString("receipts-bulk-accept-error"),
      });
    },
  });

  const handleBulkAccept = useCallback(() => {
    const suggestionIds: number[] = [];
    const receiptUuidBySuggestionId: Record<number, string> = {};
    Object.values(selectionState).forEach((row) => {
      const sid = row.suggested_match?.id;
      if (sid != null) {
        suggestionIds.push(sid);
        receiptUuidBySuggestionId[sid] = row.uuid;
      }
    });
    if (suggestionIds.length === 0) return;
    setBulkActionErrors({});
    bulkAcceptMatches.mutate({ suggestionIds, receiptUuidBySuggestionId });
  }, [bulkAcceptMatches, selectionState]);

  const bulkRejectMatches = useBulkRejectMatches({
    onSuccess: (response: BulkRejectResponse, variables) => {
      const { summary, results } = response;
      const errors: Record<string, string> = {};
      results.forEach((r) => {
        if (r.status === "failed") {
          const receiptUuid =
            variables.receiptUuidBySuggestionId[r.suggestion_id];
          if (receiptUuid) errors[receiptUuid] = r.error_message;
        }
      });
      setBulkActionErrors(errors);

      if (summary.failed === 0) {
        sendNotification({
          type: "success",
          text: l10n.getString("receipts-bulk-reject-success", {
            count: summary.succeeded,
          }),
        });
      } else {
        // Partial-failure copy doubles as the "all failed (server)" copy
        // — "0 rejected, N failed." is accurate and avoids conflating
        // per-item conflicts with a true request-level failure (handled
        // separately in onError).
        sendNotification({
          type: "negative",
          text: l10n.getString("receipts-bulk-reject-partial", {
            succeeded: summary.succeeded,
            failed: summary.failed,
          }),
        });
      }

      const succeededReceiptUuids = new Set(
        results.filter((r) => r.status === "success").map((r) => r.receipt_uuid)
      );
      setSelectionState((prev) => {
        const next: typeof prev = {};
        Object.entries(prev).forEach(([uuid, row]) => {
          if (!succeededReceiptUuids.has(uuid)) next[uuid] = row;
        });
        return next;
      });
    },
    onError: () => {
      sendNotification({
        type: "negative",
        text: l10n.getString("receipts-bulk-reject-error"),
      });
    },
  });

  const handleBulkReject = useCallback(() => {
    const suggestionIds: number[] = [];
    const receiptUuidBySuggestionId: Record<number, string> = {};
    Object.values(selectionState).forEach((row) => {
      const sid = row.suggested_match?.id;
      if (sid != null) {
        suggestionIds.push(sid);
        receiptUuidBySuggestionId[sid] = row.uuid;
      }
    });
    if (suggestionIds.length === 0) return;
    setBulkActionErrors({});
    bulkRejectMatches.mutate({ suggestionIds, receiptUuidBySuggestionId });
  }, [bulkRejectMatches, selectionState]);

  const handleAcceptFromTable = useCallback(
    ({
      suggestionId,
      receiptUuid,
    }: {
      suggestionId: number;
      receiptUuid: string;
    }) => {
      acceptMatch.mutate({ suggestionId, receiptUuid });
    },
    [acceptMatch]
  );

  const handleRejectFromTable = useCallback(
    ({
      suggestionId,
      receiptUuid,
    }: {
      suggestionId: number;
      receiptUuid: string;
    }) => {
      rejectMatch.mutate({ suggestionId, receiptUuid });
    },
    [rejectMatch]
  );

  const snapshotNextDrawerUuid = useCallback(
    (receiptUuid: string) => {
      const currentIndex = pendingReceiptUuids.indexOf(receiptUuid);
      nextDrawerUuidRef.current =
        currentIndex >= 0 && currentIndex < pendingReceiptUuids.length - 1
          ? pendingReceiptUuids[currentIndex + 1]
          : null;
    },
    [pendingReceiptUuids]
  );

  const handleAcceptFromDrawer = useCallback(
    ({
      suggestionId,
      receiptUuid,
    }: {
      suggestionId: number;
      receiptUuid: string;
    }) => {
      snapshotNextDrawerUuid(receiptUuid);
      acceptMatch.mutate({ suggestionId, receiptUuid });
    },
    [acceptMatch, snapshotNextDrawerUuid]
  );

  const handleRejectFromDrawer = useCallback(
    ({
      suggestionId,
      receiptUuid,
    }: {
      suggestionId: number;
      receiptUuid: string;
    }) => {
      snapshotNextDrawerUuid(receiptUuid);
      rejectMatch.mutate({ suggestionId, receiptUuid });
    },
    [rejectMatch, snapshotNextDrawerUuid]
  );

  const pendingAcceptSuggestionId = useMemo<number | null>(() => {
    if (!acceptMatch.isPending) return null;
    const vars = acceptMatch.variables;
    if (typeof vars === "number") return vars;
    if (vars && typeof vars === "object" && "suggestionId" in vars) {
      return vars.suggestionId;
    }
    return null;
  }, [acceptMatch.isPending, acceptMatch.variables]);

  const pendingRejectSuggestionId = useMemo<number | null>(() => {
    if (!rejectMatch.isPending) return null;
    const vars = rejectMatch.variables;
    if (typeof vars === "number") return vars;
    if (vars && typeof vars === "object" && "suggestionId" in vars) {
      return vars.suggestionId;
    }
    return null;
  }, [rejectMatch.isPending, rejectMatch.variables]);

  const handleRequestUnlink = useCallback(
    ({
      suggestionId,
      receiptId,
    }: {
      suggestionId: number;
      receiptId: string;
    }) => {
      setPendingUnlink({ suggestionId, receiptId });
    },
    []
  );

  const handleCancelUnlink = useCallback(() => {
    // Gate dismiss on pending so the dialog can't disappear out from under an
    // in-flight mutation — the toast lands while the dialog is still on screen.
    if (unlinkMatch.isPending) return;
    setPendingUnlink(null);
  }, [unlinkMatch.isPending]);

  const handleConfirmUnlink = useCallback(() => {
    if (!pendingUnlink || unlinkMatch.isPending) return;
    unlinkMatch.mutate({
      suggestionId: pendingUnlink.suggestionId,
      receiptUuid: pendingUnlink.receiptId,
    });
  }, [pendingUnlink, unlinkMatch]);

  const handleUploadSuccess = useCallback((uuid: string) => {
    setPollForUuid(uuid);
  }, []);
  const handleReceiptRemoved = useCallback(() => {
    // The removed receipt was already surfaced in the list (post-extraction);
    // refetch every bucket so it drops out and the stat-card counts update.
    pending.refetch();
    matched.refetch();
    unmatched.refetch();
  }, [pending, matched, unmatched]);
  const handleCloseDrawer = useCallback(() => {
    // Cancel any pending auto-advance so an in-flight accept doesn't reopen
    // the drawer at the next pending uuid after the user explicitly closes it.
    nextDrawerUuidRef.current = undefined;
    setSelectedReceiptUuid(null);
  }, []);
  const handleSelectReceipt = useCallback((uuid: string) => {
    setSelectedReceiptUuid(uuid);
    setSelectedMatchedUuid(null);
    setSelectedMatchedTransaction(null);
  }, []);
  const handleSelectMatched = useCallback(
    (uuid: string) => {
      const row = receipts.find((r) => r.uuid === uuid);
      setSelectedMatchedUuid(uuid);
      setSelectedMatchedTransaction(row?.suggested_match ?? null);
      setSelectedReceiptUuid(null);
    },
    [receipts]
  );
  const handleCloseMatchedDrawer = useCallback(() => {
    // When the unlink-confirmation Dialog is open, both it and the Drawer
    // receive the same window-level Escape keydown (NDS doesn't stop
    // propagation). Guard the drawer dismiss so Escape only closes the topmost
    // layer (the dialog) instead of tearing down both at once.
    if (pendingUnlink) return;
    setSelectedMatchedUuid(null);
    setSelectedMatchedTransaction(null);
  }, [pendingUnlink]);

  const handleSelectUnmatched = useCallback(
    (uuid: string) => setSelectedUnmatchedUuid(uuid),
    []
  );
  const handleCloseUnmatchedDrawer = useCallback(
    () => setSelectedUnmatchedUuid(null),
    []
  );

  const selectedUnmatchedRow = useMemo(
    () =>
      selectedUnmatchedUuid
        ? receipts.find((row) => row.uuid === selectedUnmatchedUuid) ?? null
        : null,
    [receipts, selectedUnmatchedUuid]
  );

  const renderMatchedTab = () => {
    if (receipts.length === 0) {
      return (
        <EmptyState
          heading={l10n.getString("receipts-empty-matched-heading")}
          body={l10n.getString("receipts-empty-matched-body")}
          testId="receipts-empty-state-matched"
        />
      );
    }
    return (
      <MatchedTable
        rows={receipts}
        onRowClick={handleSelectMatched}
        onUnlink={canWrite ? handleRequestUnlink : undefined}
      />
    );
  };

  const renderUnmatchedTab = () => {
    if (receipts.length === 0) {
      return (
        <EmptyState
          heading={l10n.getString("receipts-empty-unmatched-heading")}
          body={l10n.getString("receipts-empty-unmatched-body")}
          testId="receipts-empty-state-unmatched"
        />
      );
    }
    return (
      <UnmatchedTable rows={receipts} onRowClick={handleSelectUnmatched} />
    );
  };

  const renderTabContent = () => {
    if (bucket === ReceiptBucket.MATCHED) return renderMatchedTab();
    if (bucket === ReceiptBucket.UNMATCHED) return renderUnmatchedTab();
    return (
      <TabContent
        rows={receipts}
        emptyHeading={l10n.getString(EMPTY_COPY[activeTabIndex].heading)}
        emptyBody={l10n.getString(EMPTY_COPY[activeTabIndex].body)}
        emptyTestId={EMPTY_COPY[activeTabIndex].testId}
        onRowClick={
          bucket === ReceiptBucket.PENDING_REVIEW
            ? handleSelectReceipt
            : undefined
        }
        onAcceptMatch={
          canWrite && bucket === ReceiptBucket.PENDING_REVIEW
            ? handleAcceptFromTable
            : undefined
        }
        onRejectMatch={
          canWrite && bucket === ReceiptBucket.PENDING_REVIEW
            ? handleRejectFromTable
            : undefined
        }
        pendingAcceptSuggestionId={pendingAcceptSuggestionId}
        pendingRejectSuggestionId={pendingRejectSuggestionId}
        showSelection={bucket === ReceiptBucket.PENDING_REVIEW}
        bulkActionErrors={bulkActionErrors}
      />
    );
  };

  return (
    <div className="nds-typography">
      <UploadReceiptDialog
        isOpen={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        onUploadSuccess={handleUploadSuccess}
        onReceiptRemoved={handleReceiptRemoved}
      />
      <ReceiptDetailsDrawer
        uuid={selectedReceiptUuid}
        pendingUuids={pendingReceiptUuids}
        onClose={handleCloseDrawer}
        onSelect={handleSelectReceipt}
        onAcceptMatch={canWrite ? handleAcceptFromDrawer : undefined}
        onRejectMatch={canWrite ? handleRejectFromDrawer : undefined}
        pendingAcceptSuggestionId={pendingAcceptSuggestionId}
        pendingRejectSuggestionId={pendingRejectSuggestionId}
      />
      <MatchedReceiptDrawer
        uuid={selectedMatchedUuid}
        uuids={matchedReceiptUuids}
        onSelect={handleSelectMatched}
        matchedTransaction={selectedMatchedTransaction}
        suggestionId={selectedMatchedTransaction?.id ?? null}
        onUnlinkMatch={canWrite ? handleRequestUnlink : undefined}
        isUnlinkPending={unlinkMatch.isPending}
        onClose={handleCloseMatchedDrawer}
      />
      <UnmatchedReceiptDrawer
        uuid={selectedUnmatchedUuid}
        uuids={unmatchedReceiptUuids}
        onSelect={handleSelectUnmatched}
        row={selectedUnmatchedRow}
        onClose={handleCloseUnmatchedDrawer}
      />
      <Dialog
        isOpen={pendingUnlink !== null}
        onUserDismiss={handleCancelUnlink}
        title={l10n.getString("receipts-unlink-confirm-title")}
        footer={
          <div className={styles["unlink-confirm-footer"]}>
            <Button
              kind="secondary"
              label={l10n.getString("receipts-unlink-confirm-cancel")}
              onClick={handleCancelUnlink}
              disabled={unlinkMatch.isPending}
              testId="receipts-unlink-confirm-cancel"
            />
            <Button
              kind="primary"
              label={l10n.getString("receipts-unlink-confirm-confirm")}
              onClick={handleConfirmUnlink}
              isLoading={unlinkMatch.isPending}
              disabled={unlinkMatch.isPending}
              testId="receipts-unlink-confirm-confirm"
            />
          </div>
        }
        testId="receipts-unlink-confirm"
      >
        <p className="fontColor--primary">
          {l10n.getString("receipts-unlink-confirm-body")}
        </p>
      </Dialog>
      <PageLayout
        header={
          <ResponsiveFlex direction="column" toRowAt="m" gapSize="m">
            <div className={styles["heading-content"]}>
              <h1 className="fontFamily--heading">
                {l10n.getString("receipts-page-title")}
              </h1>
              <p className="fontColor--secondary fontSize--m margin--top--xs">
                {l10n.getString("receipts-page-subhead")}
              </p>
            </div>
            {canWrite && (
              <div className={styles["upload-button-wrapper"]}>
                <Button
                  kind="primary"
                  startIcon="plus"
                  label={l10n.getString("receipts-upload-button")}
                  onClick={() => setIsUploadDialogOpen(true)}
                  testId="receipts-upload-button"
                />
              </div>
            )}
          </ResponsiveFlex>
        }
      >
        <div className={styles["page-body"]}>
          <div className={styles["stat-cards"]}>
            <StatCard
              label={l10n.getString("receipts-stat-pending-review")}
              count={counts?.pending ?? 0}
              testId="receipts-stat-card-pending"
            />
            <StatCard
              label={l10n.getString("receipts-stat-matched")}
              count={counts?.matched ?? 0}
              testId="receipts-stat-card-matched"
            />
            <StatCard
              label={l10n.getString("receipts-stat-unmatched")}
              count={counts?.unmatched ?? 0}
              testId="receipts-stat-card-unmatched"
            />
          </div>
          <div className={styles["tabs-section"]}>
            <Tabs
              hasBorder
              testId="receipts-tabs"
              selectedIndex={activeTabIndex}
              onTabChange={setActiveTabIndex}
            >
              <Tabs.List>
                <Tabs.Tab
                  label={getTabLabel(
                    "receipts-tab-pending-review",
                    "receipts-stat-pending-review",
                    counts?.pending
                  )}
                  tabId={RECEIPTS_TAB_IDS.pending}
                  testId="receipts-tab-pending"
                />
                <Tabs.Tab
                  label={getTabLabel(
                    "receipts-tab-matched",
                    "receipts-stat-matched",
                    counts?.matched
                  )}
                  tabId={RECEIPTS_TAB_IDS.matched}
                  testId="receipts-tab-matched"
                />
                <Tabs.Tab
                  label={getTabLabel(
                    "receipts-tab-unmatched",
                    "receipts-stat-unmatched",
                    counts?.unmatched
                  )}
                  tabId={RECEIPTS_TAB_IDS.unmatched}
                  testId="receipts-tab-unmatched"
                />
              </Tabs.List>
            </Tabs>
            {total > 0 && (
              <div className={styles["sort-row"]}>
                <ReceiptsSortDropdown
                  value={sort}
                  onChange={handleSortChange}
                />
              </div>
            )}
            {renderTabContent()}
            {total > limit && (
              <div className={styles["pagination-row"]}>
                <Pagination
                  testId="receipts-table-pagination"
                  totalPages={Math.ceil(total / limit)}
                  selectedPage={Math.floor(offset / limit) + 1}
                  onPageChange={(page: number) =>
                    handlePageChange((page - 1) * limit)
                  }
                />
              </div>
            )}
          </div>
        </div>
      </PageLayout>
      <BulkActionSnackbar
        onAccept={
          bucket === ReceiptBucket.PENDING_REVIEW ? handleBulkAccept : undefined
        }
        onReject={
          bucket === ReceiptBucket.PENDING_REVIEW ? handleBulkReject : undefined
        }
        isAccepting={bulkAcceptMatches.isPending}
        isRejecting={bulkRejectMatches.isPending}
      />
    </div>
  );
};

const ReceiptsPage = () => (
  <SelectionProvider>
    <ReceiptsPageContent />
  </SelectionProvider>
);

export default ReceiptsPage;
